import { NavigateFunction } from 'react-router-dom';
import { hasRole, pickPrimaryRole } from '@court-sync/shared';
import { subscriptionService } from '../services/subscription';
import { AuthService } from '../services/auth';
import { useAuthStore } from '../store/auth';
import { buildFinishSignupPathFromSessionStorage } from './billingInterval';
import { userHasOrgEntity } from './platformLoginRedirect';

export interface UserSetupStatus {
  /** User has created an academy and/or club organization entity */
  hasOrgEntity: boolean;
  hasSubscription: boolean;
  /** @deprecated Use hasOrgEntity */
  hasAcademy: boolean;
}

export interface SocialLoginUser {
  email: string;
  academyId?: string;
  termsAcceptedAt?: Date;
  privacyAcceptedAt?: Date;
  roles?: string[];
  rolesWithAcademy?: Array<{
    role: import('@court-sync/shared').UserRole;
    academyId?: string | null;
    clubId?: string | null;
  }>;
}

function isClubOnlyOwner(user?: SocialLoginUser): boolean {
  if (!user) return false;
  const hasClub =
    hasRole(user as Parameters<typeof hasRole>[0], 'club_owner') ||
    user.roles?.includes('club_owner');
  const hasAcademy =
    !!user.academyId ||
    hasRole(user as Parameters<typeof hasRole>[0], 'academy_owner');
  return hasClub && !hasAcademy;
}

/**
 * Authenticates user with the specified social provider
 */
export async function authenticateWithProvider(
  provider: 'google' | 'facebook' | 'github',
  role: 'guardian' | 'academy_owner' = 'academy_owner'
): Promise<SocialLoginUser> {
  const { loginWithGoogle, loginWithFacebook, loginWithGithub } = useAuthStore.getState();

  switch (provider) {
    case 'google':
      return await loginWithGoogle(role);
    case 'facebook':
      return await loginWithFacebook(role);
    case 'github':
      return await loginWithGithub(role);
    default:
      throw new Error(`Unsupported provider: ${provider}`);
  }
}

/**
 * Refreshes user profile and updates the auth store
 * Returns the refreshed user or the original user if refresh fails
 */
export async function refreshAndUpdateUserProfile(
  initialUser: SocialLoginUser
): Promise<SocialLoginUser> {
  try {
    const { user, setUser } = useAuthStore.getState();

    if (user) {
      return user;
    }

    const refreshedUser = await AuthService.refreshUserProfile();

    if (refreshedUser) {
      setUser(refreshedUser);
      return refreshedUser;
    }

    return initialUser;
  } catch {
    return initialUser;
  }
}

/**
 * Checks the user's setup status (organization entity and subscription)
 */
export async function checkUserSetupStatus(user: SocialLoginUser): Promise<UserSetupStatus> {
  try {
    const subscription = await subscriptionService.getCurrentSubscription();
    const hasSubscription = !!(
      subscription &&
      (subscription.subscription?.status === 'active' ||
        subscription.subscription?.status === 'trialing')
    );
    const hasOrgEntity = userHasOrgEntity(user);

    return { hasOrgEntity, hasSubscription, hasAcademy: hasOrgEntity };
  } catch {
    const hasOrgEntity = userHasOrgEntity(user);
    return {
      hasOrgEntity,
      hasSubscription: false,
      hasAcademy: hasOrgEntity,
    };
  }
}

/**
 * Determines the appropriate navigation route based on user setup status
 */
export function getNavigationRoute(
  setupStatus: UserSetupStatus,
  userEmail: string,
  planId?: string,
  user?: SocialLoginUser
): { path: string; state?: { email: string; message: string; isSocialLogin: boolean; returnUrl?: string } } {
  const { hasOrgEntity, hasSubscription } = setupStatus;

  const hasAcceptedTerms = user?.termsAcceptedAt && user?.privacyAcceptedAt;

  if (hasOrgEntity && hasSubscription) {
    if (isClubOnlyOwner(user)) {
      return { path: '/admin/dashboard' };
    }

    const role = user?.rolesWithAcademy?.length
      ? pickPrimaryRole(
          user.rolesWithAcademy as Array<{
            role: import('@court-sync/shared').UserRole;
            academyId?: string | null;
          }>
        )
      : (user?.roles?.[0] ?? 'guardian');
    if (role === 'admin') {
      return { path: '/backoffice/risk' };
    }
    if (role === 'academy_owner' || role === 'club_owner') {
      return { path: '/admin/dashboard' };
    }
    return { path: '/org-picker' };
  }

  if (!hasOrgEntity) {
    return {
      path: '/academy-setup',
      state: {
        email: userEmail,
        message: "Welcome! Let's set up your organization on CourtSync.",
        isSocialLogin: true,
      },
    };
  }

  if (!hasAcceptedTerms) {
    let returnUrl = '/dashboard';
    if (!hasSubscription) {
      returnUrl = planId
        ? buildFinishSignupPathFromSessionStorage(planId)
        : buildFinishSignupPathFromSessionStorage();
    }

    return {
      path: '/legal-acceptance',
      state: {
        email: userEmail,
        message: 'Please accept our Terms of Service and Privacy Policy to continue.',
        isSocialLogin: true,
        returnUrl,
      },
    };
  }

  const finishSignupPath = buildFinishSignupPathFromSessionStorage(planId);

  return {
    path: finishSignupPath,
    state: {
      email: userEmail,
      message: 'Welcome back! Please choose your subscription plan to continue.',
      isSocialLogin: true,
    },
  };
}

/**
 * Navigates after social registration when the user is already authenticated.
 */
export async function navigateAfterSocialRegistration(
  navigate: NavigateFunction,
  initialUser: SocialLoginUser,
  planId?: string
): Promise<void> {
  const currentUser = await refreshAndUpdateUserProfile(initialUser);
  const setupStatus = await checkUserSetupStatus(currentUser);
  const storedPlanId =
    planId || (typeof window !== 'undefined' ? sessionStorage.getItem('selectedPlanId') : null);

  const route = getNavigationRoute(
    setupStatus,
    currentUser.email,
    storedPlanId || undefined,
    currentUser
  );
  navigate(route.path, route.state ? { state: route.state } : undefined);
}

/**
 * Handles the complete social login flow
 */
export async function handleSocialLoginFlow(
  provider: 'google' | 'facebook' | 'github',
  navigate: NavigateFunction,
  planId?: string
): Promise<void> {
  const initialUser = await authenticateWithProvider(provider);
  await navigateAfterSocialRegistration(navigate, initialUser, planId);
}
